/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import {
  type PlaceholderDataFunction,
  type QueryClient,
  type QueryFunctionContext,
  type QueryObserverOptions,
  useQuery,
} from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';

type InternalQueryOptions = {
  enabled?: boolean;
  gcTime?: number;
  staleTime?: number;
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
  retryDelay?: number | ((retryAttempt: number, error: unknown) => number);
  refetchOnMount?: QueryObserverOptions['refetchOnMount'];
};

export type CustomUseQueryOptions<
  Data,
  SelectData,
  QueryKey extends readonly unknown[] = readonly unknown[],
> = {
  select?: (data: Data) => SelectData;
  placeholderData?: Data | PlaceholderDataFunction<Data, Error, Data, QueryKey>;
};

export function createQuery<Data, Options>(
  key: string,
  get: (options: Options, axiosInstance?: AxiosInstance) => Promise<Data>,
) {
  const query = {
    getPrimaryKey: () => key,
    getFullKey: (options: Options) => [key, options] as const,
  };

  const queryFn = async (
    { queryKey }: QueryFunctionContext<ReturnType<typeof query.getFullKey>>,
    axiosInstance?: AxiosInstance,
  ) => {
    const [, options] = queryKey;
    return get(options, axiosInstance);
  };

  function useData<SelectData = Data>(
    options: Options & InternalQueryOptions & CustomUseQueryOptions<Data, SelectData>,
  ) {
    const {
      enabled,
      select,
      placeholderData,
      gcTime,
      staleTime,
      retry,
      retryDelay,
      refetchOnMount,
      ...rest
    } = options || {};

    const optionsWithoutUndefined = Object.entries({
      enabled,
      placeholderData,
      select,
      gcTime,
      staleTime,
      retry,
      retryDelay,
      refetchOnMount,
    }).reduce<Record<string, unknown>>((entry, [key, value]: [string, unknown]) => {
      if (value !== undefined) {
        entry[key] = value;
      }
      return entry;
    }, {});

    return useQuery({
      ...optionsWithoutUndefined,
      // @ts-expect-error: the type of options is conditionally set, so TypeScript is unable to infer it
      queryKey: query.getFullKey(rest),
      queryFn,
      select,
    });
  }

  const prefetch = (queryClient: QueryClient, options: Options, axiosInstance: AxiosInstance) => {
    return queryClient.prefetchQuery({
      queryKey: query.getFullKey(options),
      queryFn: (ctx) => queryFn(ctx, axiosInstance),
    });
  };

  const getFromCache = (queryClient: QueryClient, options: Options) => {
    return queryClient.getQueryData<Data>(query.getFullKey(options));
  };

  return { query, useData, prefetch, getFromCache } as const;
}
